import { useState } from "react";
import { Check, ExternalLink, Gift, Inbox, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { money } from "@/lib/format";
import { EmptyState } from "@/components/common";
import { KIDS_RGB, useBusy } from "@/components/kids/kidsUi";

export function OrderLinks({ item, hubId, onDone }) {
  const ordered = async () => {
    try {
      await api.post(`/organizations/${hubId}/wish-items/${item.id}/ordered`);
      toast.success("Marked as ordered");
      onDone();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={`order-links-${item.id}`}>
      {item.link && <a className="q-btn q-btn-secondary py-1.5 text-xs" href={item.link} target="_blank" rel="noreferrer" data-testid={`order-item-link-${item.id}`}><ExternalLink className="h-3.5 w-3.5" /> Item link</a>}
      {item.wishlist_url && <a className="q-btn q-btn-secondary py-1.5 text-xs" href={item.wishlist_url} target="_blank" rel="noreferrer" data-testid={`order-wishlist-link-${item.id}`}><ExternalLink className="h-3.5 w-3.5" /> Amazon wish list</a>}
      {item.status === "approved" && <button className="q-btn q-btn-primary py-1.5 text-xs" onClick={ordered} data-testid={`mark-ordered-btn-${item.id}`}>Mark ordered</button>}
      {item.status === "ordered" && <span className="text-xs font-bold text-q-muted">Ordered</span>}
    </div>
  );
}

const REQ_LABEL = { save_use: "Use Save money", give: "Give", screen: "Screen time" };
const reqTitle = (a) => ({ save_use: `Use ${money(a.amount)} of Save: ${a.reason}`, give: `Give ${money(a.amount)} to ${a.reason}`, screen: `${a.minutes} min screen time${a.reason ? ` · ${a.reason}` : ""}` }[a.kind]);

function ApprovalRow({ a, hubId, onChanged }) {
  const [note, setNote] = useState("");
  const [rejecting, setRejecting] = useState(false);
  const [price, setPrice] = useState(String(a.price ?? ""));
  const [bought, setBought] = useState(null);
  const [busy, run] = useBusy();
  const isReq = ["save_use", "give", "screen"].includes(a.kind);
  const base = isReq ? `/organizations/${hubId}/kid-requests/${a.id}` : a.kind === "chore" ? `/organizations/${hubId}/chore-instances/${a.id}` : `/organizations/${hubId}/wish-items/${a.id}`;

  const approve = () => run("approve", async () => {
    try {
      const { data } = await api.post(`${base}/approve`, a.kind === "wish" ? { final_price: price } : {});
      toast.success(isReq ? `Approved for ${a.kid_name}` : a.kind === "chore" ? `${a.kid_name} earned ${money(a.reward)}` : `Approved: ${a.name}`);
      if (a.kind === "wish") setBought(data.item); else onChanged();
    } catch (e) { toast.error(errMsg(e)); }
  });
  const reject = () => run("reject", async () => {
    try {
      await api.post(`${base}/${a.kind === "chore" ? "reject" : "decline"}`, { note });
      if (isReq) { toast.success("Declined"); onChanged(); return; }
      toast.success(a.kind === "chore" ? "Sent back to try again" : "Declined");
      onChanged();
    } catch (e) { toast.error(errMsg(e)); }
  });

  if (bought) {
    return (
      <li className="q-card space-y-3" data-testid={`approval-${a.id}`}>
        <p className="text-sm font-semibold text-q-text" data-testid={`approval-bought-${a.id}`}>Approved. {money(bought.final_price)} came out of {a.kid_name}'s piggy bank. Order it on Amazon:</p>
        <OrderLinks item={bought} hubId={hubId} onDone={onChanged} />
        <button className="q-btn q-btn-ghost py-1 text-xs" onClick={onChanged} data-testid={`approval-done-${a.id}`}>Done</button>
      </li>
    );
  }
  return (
    <li className="q-card flex flex-col gap-3 sm:flex-row sm:items-center" data-testid={`approval-${a.id}`} data-kind={a.kind}>
      <span className="inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl text-2xl" style={{ background: `rgba(${KIDS_RGB},0.12)` }}>
        {a.kind === "chore" ? a.icon : <Gift className="h-5 w-5" style={{ color: `rgb(${KIDS_RGB})` }} />}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate font-semibold text-q-text" data-testid={`approval-title-${a.id}`}>{reqTitle(a) || (a.kind === "chore" ? a.title : `I want this: ${a.name}`)}</p>
        <p className="text-xs text-q-muted">{a.kid_name} · {isReq ? REQ_LABEL[a.kind] : a.kind === "chore" ? `${money(a.reward)} reward${a.minutes ? ` + ${a.minutes} min` : ""}` : `${money(a.price)} wish`}</p>
        {rejecting && <input className="q-input mt-2" placeholder={a.kind === "chore" ? "Note, e.g. Bin is still full" : "Note, e.g. Let's wait for your birthday"} value={note} onChange={(e) => setNote(e.target.value)} data-testid={`approval-note-${a.id}`} />}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {a.kind === "wish" && !rejecting && (
          <label className="flex items-center gap-1.5 text-xs font-semibold text-q-muted">Final $
            <input type="number" step="0.01" min="0.01" className="q-input w-24 py-1.5" value={price} onChange={(e) => setPrice(e.target.value)} data-testid={`approval-price-${a.id}`} />
          </label>
        )}
        {rejecting ? (
          <>
            <button className="q-btn q-btn-danger py-2" onClick={reject} disabled={!!busy} data-testid={`approval-reject-confirm-${a.id}`}>{a.kind === "chore" ? "Send back" : "Decline"}</button>
            <button className="q-btn q-btn-ghost py-2" onClick={() => setRejecting(false)} data-testid={`approval-reject-cancel-${a.id}`}>Cancel</button>
          </>
        ) : (
          <>
            <button className="q-btn q-btn-primary py-2" onClick={approve} disabled={!!busy} data-testid={`approval-approve-${a.id}`}><Check className="h-4 w-4" /> Approve</button>
            <button className="q-btn q-btn-secondary py-2" onClick={() => setRejecting(true)} data-testid={`approval-reject-${a.id}`}><X className="h-4 w-4" /> {a.kind === "chore" ? "Reject" : "Decline"}</button>
          </>
        )}
      </div>
    </li>
  );
}

export default function Approvals({ items, hubId, canManage, onChanged }) {
  return (
    <section id="approvals" className="mb-10" data-testid="approvals">
      <h2 className="q-eyebrow mb-4">Approvals · <span data-testid="approvals-count">{items.length}</span></h2>
      {items.length === 0 ? <EmptyState Icon={Inbox} title="All caught up" hint="Finished chores, wish, Save, Give and screen-time requests show up here." rgb={KIDS_RGB} testid="approvals-empty" /> : (
        <ul className="space-y-3">
          {items.map((a) => canManage ? <ApprovalRow key={`${a.kind}-${a.id}`} a={a} hubId={hubId} onChanged={onChanged} /> : (
            <li key={a.id} className="q-card text-sm text-q-text" data-testid={`approval-${a.id}`}>{reqTitle(a) || (a.kind === "chore" ? `${a.icon} ${a.title}` : `I want this: ${a.name}`)} · {a.kid_name}</li>
          ))}
        </ul>
      )}
    </section>
  );
}
